import { Elements } from '@stripe/react-stripe-js';
import { useQuery } from '@tanstack/react-query';
import { countries } from 'countries-list';
import { BreadcrumbItem } from '@ui/Breadcrumb';
import Button from '@ui/Button';
import Chip from '@ui/Chip';
import DropdownMenu from '@ui/DropdownMenu';
import ProductCarousel from '@ui/ProductCarousel';
import { getNumber } from '@util/createListingHelpers';
import { BidDocument } from '@util/firestore/bid';
import { deleteListing } from '@util/firestore/products';
import { getCheapestEstimate } from '@util/firestore/shipengine';
import { getUserDocument } from '@util/firestore/users';
import { formatCurrency } from '@util/index';
import { ProductDocument } from '@util/types/firestore/products';
import { MasterDocument } from '@util/firestore/master';

import { useAuth } from 'context/AuthContext';
import { useStripeContext } from 'context/StripeContext';
import { useToastContext } from 'context/ToastContext';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { getBrandByName } from 'pages/api/brands';
import { useEffect, useState } from 'react';
import SellerCard from './cards/SellerCard';
import FirstVisitFollowPopup from '@c/FirstVisitFollowPopup';
import FormCurrencyInputFancy from './forms/controls/FormCurrencyInputFancy';
import {
  FlagIcon,
  ShareAltIcon,
  ShieldStarIcon,
  ShieldCheckIcon,
} from './icons';
import AcceptedCards from './icons/acceptedCards';
import { AffirmMessaging } from './affirm/AffirmMessaging';
import { normalizeCountryCode } from '@util/affirm/eligibility';
import HeartIconButton from './icons/buttons/HeartButton';
import ConfirmModal from './modals/ConfirmModal';
import EstimateShippingModal from './modals/EstimateShippingModal';
import MakeAnOfferModal from './modals/MakeAnOfferModal';
import ProductViewerModal from './modals/ProductViewerModal';
import ReportItemModal from './modals/ReportItemModal';
import ReturnPolicyModal from './modals/ReturnPolicyModal';
import ShareModal from './modals/ShareModal';
import { AuctionStatus } from './products/AuctionStatus';
import { BidHistoryTable } from './products/BidHistoryTable';
import { BuyAddOfferButtons } from './products/BuyAddOfferButtons';
import ComingSoonBanner from './products/ComingSoonBanner';
import RenewListingModal from '@c/modals/RenewListingModal';
import ProtectionPolicyModal from './modals/ProtectionPolicyModal';
import ProductSchema from './schema/ProductSchema';

import ProductOverviewCard from './cards/ProductOverviewCard';
import ProductDescriptionCard from './cards/ProductDescriptionCard';

import { PolicyDocument } from '@util/firestore/settings';
import { useEffectivePrice } from 'context/StoreSaleContext';
import type { SellerBadges } from '@util/sellerBadges/computeSellerBadges';

interface ProductDetailsProps {
  product: ProductDocument;
  master: MasterDocument[];
  breadcrumbs: BreadcrumbItem[];
  bids: BidDocument[];
  userBids: BidDocument[];
  highestBid?: BidDocument;
  conditionDescription?: string;
  protectionPolicyContent?: string;
  resolvedReturnPolicy?: PolicyDocument | null;
  sellerBadgeResult?: { sellerId: string; badges: SellerBadges };
}

export const ProductDetails = ({
  product,
  master,
  breadcrumbs,
  bids,
  userBids,
  highestBid,
  conditionDescription,
  protectionPolicyContent,
  resolvedReturnPolicy,
  sellerBadgeResult,
}: ProductDetailsProps) => {
  const storePrice = useEffectivePrice(product);
  const displayPrice = storePrice?.applied ? storePrice.unitCents / 100 : product.price;
  const { stripePromise } = useStripeContext();

  const [shipping, setShipping] = useState<
    | {
        code: string;
        cost: number;
        name: string;
        estimated?: boolean;
      }
    | undefined
  >(product.shipping_costs?.[0]);

  const [openModal, setOpenModal] = useState<
    | 'protectionPlan'
    | 'returnPolicy'
    | 'reportItem'
    | 'share'
    | 'product-viewer'
    | 'estimate-shipping'
    | 'confirm'
    | 'bid'
    | ''
  >('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showEditBid, setShowEditBid] = useState(false);

  const router = useRouter();
  const { userDoc, user } = useAuth();

  // Hydration-safe: the auction time-window checks below depend on the wall
  // clock, which differs between the SSR render and the browser. Each such
  // check is gated on `mounted` so the server and first client render agree
  // (both render the neutral fallthrough), then the real branch is chosen
  // after mount. We must NOT compare a real timestamp against an epoch-0
  // sentinel: 0 makes every started/ended auction look "not started", so
  // cached/no-JS HTML would wrongly show "Auction Starting Soon" and hide the
  // live-bid CTA. Calling Date.now() only under `mounted` avoids that entirely.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const [overflowActive, setOverflowActive] = useState(false);

  // Signed-in buyer's saved country, shared by the flat-rate shipping-band
  // lookup below AND the AffirmMessaging mount further down (GB #1372
  // post-merge follow-up on PR #1451) so the two can never disagree about
  // which country the PDP thinks the buyer is in. Same derivation as
  // CartSlideOutV3's buyerCountry: normalized (trim/uppercase, '' ->
  // undefined) so userDoc.country's new-user '' default can't shadow the
  // saved-address fallback and silently produce a raw '' that fails the
  // shipping_costs.find() below. Guests (and signed-in users with no saved
  // country/address) stay undefined, which is each call site's existing
  // default behavior.
  const buyerCountry =
    normalizeCountryCode(userDoc?.country) ??
    normalizeCountryCode(userDoc?.addresses?.[0]?.country_code);

  useEffect(() => {
    if (product.is_flat_rate === false) {
      const addy =
        userDoc?.addresses?.find((a) => a.is_default) ??
        userDoc?.addresses?.find((a) => a.is_shop_location) ??
        userDoc?.addresses?.[0];
      if (addy)
        getCheapestEstimate(
          product.id,
          addy.postal_code,
          addy.country_code
        ).then((cost) => {
          setShipping({
            cost,
            name: addy.postal_code,
            code: addy.country_code,
            estimated: true,
          });
        });
    } else if (buyerCountry && product.shipping_costs?.length) {
      const foundShipping = product.shipping_costs.find(
        (s) => s.code === buyerCountry
      );
      if (foundShipping) setShipping(foundShipping);
    }
  }, [product, userDoc, buyerCountry]);

  const { data: seller, isLoading: isLoadingSeller } = useQuery(
    ['seller', product.seller_id],
    () => getUserDocument({ uid: product.seller_id }),
    {
      enabled: !!product.seller_id,
    }
  );

  const { data: brand } = useQuery(
    ['brand', product.brand],
    () => getBrandByName(product.brand),
    {
      enabled: !!product.brand,
    }
  );

  const [updateBidPrice, setUpdateBidPrice] = useState('');
  const { showErrorToast, showSuccessToast } = useToastContext();

  const [renewModal, setRenewModal] = useState<boolean>(false);

  // Single source of truth for "can this listing actually be bought right
  // now" (Draft / On Vacation / SOLD all key off this — see products.service.ts
  // updateDoc(is_draft: true, out_of_stock: true) and the vacation-mode "sold"
  // fingerprint, both of which route through out_of_stock/date_added rather
  // than their own flags). Reused below by both the SOLD/Vacation/Draft
  // banner and the AffirmMessaging mount guard so the two can never drift —
  // do not write a second, parallel purchasability check in this file.
  const isPurchasable = !product.out_of_stock && !!product.date_added;

  return (
    <div
      className="
      flex w-full 
      flex-col gap-[3rem]
      overflow-x-hidden sm:gap-[4.8rem] 
      md:flex-row
    "
    >
      {/* GB #691: first-visit one-time Follow nudge, keyed to this listing's
          seller. Self-gates (once ever across all seller pages; never on the
          seller's own listing); fixed-position so tree placement is cosmetic. */}
      <FirstVisitFollowPopup
        sellerId={product.seller_id}
        sellerName={seller?.username}
      />
      {/* 📷 Image & Seller Section */}
      <div className="w-full md:w-[50%] xl:w-[60%]">
        {product.images?.length ? (
          <div className="relative  lg:mt-[1rem]">
            <div className="absolute right-4 top-4 z-30 cursor-pointer">
              <HeartIconButton
                itemId={product.id}
                initialCount={product.favorite_count ?? 0}
              />
            </div>
            <ProductCarousel
              product={product}
              openProductViewer={(index: number) => {
                setCurrentImageIndex(index);
                setOpenModal('product-viewer');
              }}
            />
            <ProductViewerModal
              isOpen={openModal === 'product-viewer'}
              dismiss={() => setOpenModal('')}
              images={product.images}
              initialImage={currentImageIndex}
            />
          </div>
        ) : (
          <div>
            <Image
              alt="No Images Found"
              width={500}
              height={500}
              src="/not_found.svg"
            ></Image>
          </div>
        )}
        <div className="flex flex-row items-center justify-between px-4 sm:mt-[2rem] sm:px-0">
          <div className="flex flex-col sm:flex-row">
            <p>Have a similar item?</p>
            <Link
              className="font-semibold text-brand-blue sm:ml-2"
              title="Sell your used camera gear on GearFocus"
              target="_blank"
              href={
                userDoc?.account_activated ? '/sell/create-listing' : '/sell'
              }
            >
              Sell yours.
            </Link>
          </div>
          <Button
            leadingIcon={<ShareAltIcon />}
            text="Share"
            type="text"
            width="small"
            noXPadding
            onClick={() => setOpenModal('share')}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between">
          <div className="flex flex-wrap gap-[2rem]">
            <Button
              text="Return Policy"
              type="text"
              width="small"
              noXPadding={true}
              onClick={() => {
                setOpenModal('returnPolicy');
              }}
              leadingIcon={<ShieldStarIcon />}
            ></Button>
            <Button
              text="Protection Plan"
              type="text"
              width="small"
              noXPadding={true}
              onClick={() => {
                setOpenModal('protectionPlan');
              }}
              leadingIcon={<ShieldCheckIcon width={19} height={18} />}
            ></Button>
          </div>
          <Button
            text="Report Listing"
            type="text"
            width="small"
            noXPadding={true}
            onClick={() => {
              setOpenModal('reportItem');
            }}
            leadingIcon={<FlagIcon />}
          ></Button>
        </div>
        <hr className="sm:mt-[2rem]"></hr>
        <div className="mt-[3rem] hidden md:block">
          {isLoadingSeller && (
            <div className="flex flex-row items-center justify-start">
              {/* skeleton */}
              <div className="flex animate-pulse flex-row items-center gap-[1.6rem]">
                <div className="h-[4.8rem] w-[4.8rem] rounded-full bg-gray-300"></div>
                <div className="flex flex-col gap-[0.8rem]">
                  <div className="h-[2.4rem] w-[20rem] rounded bg-gray-300"></div>
                  <div className="h-[2.4rem] w-[12rem] rounded bg-gray-300"></div>
                </div>
              </div>
            </div>
          )}
          {seller && (
            <SellerCard
              sellerBadgeResult={sellerBadgeResult}
              uid={user?.uid}
              sellerDoc={seller}
              productId={product.id}
            />
          )}
        </div>
      </div>

      {/* 📄 Product Details */}
      <div className="flex w-full flex-col gap-[1.6rem] md:w-[50%] xl:w-[40%]">
        <h1 className="text-[3.2rem] font-semibold leading-[4rem]">
          {product.title}
        </h1>

        {!product.is_expired && !product.is_auction && (
          <div className="flex flex-col items-start gap-[1.6rem] xl:flex-row">
            {storePrice?.applied ? (
              <div className="flex flex-col items-start gap-[0.8rem]">
                <div className="flex items-center gap-[0.8rem]">
                  <p className="text-[2.4rem] font-medium leading-[2.4rem]">{formatCurrency(displayPrice)}</p>
                  <Chip text={`${storePrice.applied.percentOff}% off`} color="error" />
                </div>
                <p className="text-[2.4rem] text-gray-400 line-through">{formatCurrency(product.price)}</p>
                <p className="text-[1.2rem] text-gray-600">
                  Store sale ends {new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(storePrice.applied.endsAt)}
                </p>
              </div>
            ) : product.on_sale ? (
              <div className="flex flex-col items-start gap-[0.8rem]">
                <div className="flex gap-[0.8rem]">
                  <p className="text-[2.4rem] font-medium leading-[2.4rem]">
                    {formatCurrency(product.price)}
                  </p>
                  <Chip
                    text={`${Math.round(
                      ((Number(product.og_price) - product.price) /
                        Number(product.og_price)) *
                        100
                    )}% off`}
                    color="error"
                  />
                </div>

                <p className="text-[2.4rem] font-normal leading-[2.4rem] text-gray-400 line-through">
                  {formatCurrency(product.og_price)}
                </p>
              </div>
            ) : (
              <p className="text-[2.4rem] font-medium leading-[2.4rem]">
                {formatCurrency(product.price)}
              </p>
            )}
          </div>
        )}
        {!product.is_expired && !product.is_auction && isPurchasable && (
          // Item-price-only basis — shipping/tax unknown at PDP (TASK-1372 §2.A1.2).
          // Auctions excluded: product.price isn't the buy-it-now amount there.
          // isPurchasable excluded: never advertise financing on a listing the
          // SOLD/Vacation/Draft banner below is about to show as unbuyable.
          // buyerCountry: signed-in viewers only (GB #1372 QA follow-up,
          // fresh-eyes review) — guests stay undefined, keeping the existing
          // US-assumption default.
          <AffirmMessaging amountUSD={displayPrice} buyerCountry={buyerCountry} />
        )}
        {!product.is_expired && shipping ? (
          <div className="w-fit max-w-full font-medium [&>div]:whitespace-normal">
            <Chip
              text={`+ ${formatCurrency(shipping.cost)} ${
                shipping.estimated ? 'est. shipping, before insurance' : 'shipping'
              }`}
              color="gray"
            />
          </div>
        ) : (
          !product.is_expired && (
            <small>
              <Link className="underline" href="/help?category=Shipping">
                Shipping
              </Link>{' '}
              calculated at checkout.
            </small>
          )
        )}

        {product.start_time && mounted && product.start_time > Date.now() ? (
          <div className="flex w-full flex-col items-start gap-[2rem] rounded-3xl bg-brand-primary p-[2.4rem]">
            <div className="flex w-full items-center justify-center text-brand-white">
              <h1 className="text-center text-3xl font-semibold">
                Auction Starting Soon
              </h1>
            </div>
          </div>
        ) : !isPurchasable && !product.is_auction ? (
          <div className="flex w-full flex-col items-start gap-[2rem] rounded-3xl bg-brand-secondary p-[2.4rem]">
            <div className="flex w-full items-center justify-center text-brand-white">
              <h1 className="text-center text-3xl font-semibold">
                {product.is_draft
                  ? 'Draft Listing'
                  : product.on_vacation
                  ? 'On Vacation'
                  : 'SOLD'}
              </h1>
            </div>
          </div>
        ) : user === undefined || seller === undefined ? (
          <div className="flex flex-col gap-[1.6rem]">
            <div className="flex flex-col gap-[1.6rem] xl:flex-row">
              <div className="h-[4.5rem] w-full animate-pulse rounded-full bg-brand-light-gray" />
              <div className="h-[4.5rem] w-full animate-pulse rounded-full bg-brand-light-gray" />
            </div>
            <div className="h-[4.5rem] w-full animate-pulse rounded-full bg-brand-light-gray" />
          </div>
        ) : user?.uid === product.seller_id ? (
          <div className="flex flex-col gap-[1.6rem]">
            <div className="flex flex-col gap-[1.6rem] xl:flex-row">
              {!product.is_expired ? (
                <Button
                  width="fluid"
                  type="tertiary"
                  text="Edit Listing"
                  href={`/edit-listing/${product.id}`}
                />
              ) : (
                <>
                  <Button
                    width="fluid"
                    type="tertiary"
                    text="Renew Listing"
                    onClick={() => {
                      setRenewModal(true);
                    }}
                  />
                  <RenewListingModal
                    product={product}
                    isOpen={renewModal}
                    dismiss={() => {
                      setRenewModal(false);
                    }}
                  />
                </>
              )}

              <Button
                width="fluid"
                type="decline"
                text="Delete Listing"
                onClick={() => setOpenModal('confirm')}
              />
            </div>
            <ConfirmModal
              title="Delete Listing"
              body="Are you sure you want to delete this listing?"
              buttonText="Delete"
              isOpen={openModal === 'confirm'}
              dismiss={(confirm) => {
                if (confirm)
                  deleteListing(product.id, product.is_draft)
                    .then((result) => {
                      if (result.archived) {
                        showSuccessToast(
                          'This listing has sales history, so it was archived instead of deleted.'
                        );
                      }
                      // Draft path (delete or archive): the doc is gone or
                      // no longer a draft either way, so there's nothing
                      // left to reload here for a support user — a reload
                      // would hit a 404 on a hard-deleted draft. Always
                      // navigate away. Non-draft soft-delete keeps the
                      // existing support-reload behavior below.
                      if (product.is_draft || !userDoc?.roles?.support) {
                        router.push('/dashboard/listings');
                      } else {
                        router.reload();
                      }
                    })
                    .catch((e) => {
                      showErrorToast(
                        e instanceof Error
                          ? e.message
                          : 'Delete failed. Please try again.'
                      );
                      setOpenModal('');
                    });
                else setOpenModal('');
              }}
            />
          </div>
        ) : !product.is_expired && !product.is_draft && !product.is_auction ? (
          !product.is_verified && product.seller_id !== user?.uid ? (
            <ComingSoonBanner productId={product.id} />
          ) : (
            <BuyAddOfferButtons
              product={product}
              seller={seller}
              userDoc={userDoc}
              user={user}
            />
          )
        ) : null}

        {product.is_expired ? (
          <div className="flex w-full flex-col items-start gap-[2rem] rounded-3xl bg-brand-gray p-[2.4rem]">
            <div className="flex w-full items-center justify-center text-brand-white">
              <h1 className="text-center text-3xl font-semibold">
                Listing Expired
              </h1>
            </div>
          </div>
        ) : null}

        {!product.is_expired && !product.is_draft && product.is_auction ? (
          <div className="flex flex-col gap-[1.6rem]">
            <AuctionStatus product={product} highestBid={highestBid} />
            {product.seller_id !== user?.uid &&
              mounted &&
              product.end_time! > Date.now() &&
              product.start_time! < Date.now() && (
                <>
                  {userBids.length > 0 ? (
                    <div className="flex w-full justify-center gap-[0.8rem]">
                      <FormCurrencyInputFancy
                        full
                        onChange={(e) => setUpdateBidPrice(e.target.value)}
                        value={updateBidPrice}
                      />
                      <Button
                        type="secondary"
                        text="Update Bid"
                        width="small"
                        onClick={() => {
                          if (updateBidPrice) {
                            if (
                              highestBid &&
                              getNumber(updateBidPrice) <= highestBid?.bid_price
                            ) {
                              showErrorToast(
                                'Your bid must be higher than the current bid!'
                              );
                              return;
                            }
                            if (
                              highestBid?.bid_price &&
                              getNumber(updateBidPrice) <
                                highestBid?.bid_price + 1
                            ) {
                              showErrorToast(
                                'Your bid must be at least $1 higher than the current bid!'
                              );
                              return;
                            }
                            setShowEditBid(true);
                            setOpenModal('bid');
                          }
                        }}
                      />
                    </div>
                  ) : !product.out_of_stock ? (
                    <Button
                      type="secondary"
                      text="Place a Bid"
                      width="fluid"
                      onClick={() => {
                        if (!userDoc) {
                          if (!user) {
                            // waiting for userDoc
                            router.push({
                              pathname: '/login',
                              query: { redirect: router.asPath },
                            });
                          }
                        } else {
                          setOpenModal('bid');
                        }
                      }}
                    />
                  ) : null}

                  <Elements stripe={stripePromise}>
                    <MakeAnOfferModal
                      key={openModal}
                      variant="bid"
                      isOpen={openModal === 'bid'}
                      dismiss={() => setOpenModal('')}
                      product={product}
                      highestBid={highestBid}
                      currentUserBid={userBids?.[0]}
                      isEditingBid={showEditBid}
                      bidInput={updateBidPrice}
                    />
                  </Elements>
                </>
              )}

            <BidHistoryTable bids={bids} product={product} />
          </div>
        ) : null}

        <div className="mt-[0rem] md:hidden">
          {seller && (
            <SellerCard
              sellerBadgeResult={sellerBadgeResult}
              uid={user?.uid}
              sellerDoc={seller}
              productId={product.id}
            />
          )}
        </div>

        <h3 className="mt-[2rem] text-[1.5rem] font-semibold">Description</h3>

        <ProductDescriptionCard product={product} />

        <h3 className="mt-[2rem] text-[1.5rem] font-semibold">Overview</h3>
        <ProductOverviewCard
          product={product}
          breadcrumbs={breadcrumbs}
          master={master}
          brand={brand}
          conditionDescription={conditionDescription}
        />

        <h3 className="mt-[2rem] text-[1.5rem] font-semibold">
          Shipping & Payments
        </h3>
        {shipping && !shipping.estimated && (
          <div className="flex h-[4.4rem] w-min items-center gap-[1.2rem] whitespace-nowrap rounded-full bg-brand-off-white p-[1.2rem]">
            <DropdownMenu
              label={`+ ${formatCurrency(shipping.cost)} - ${shipping.name}`}
              menuItems={
                product.shipping_costs?.map((s) => {
                  return {
                    label: `+ ${formatCurrency(s.cost)} - ${s.name}`,
                    onClick: () => {
                      /* no-op */
                    },
                  };
                }) ?? []
              }
            />
          </div>
        )}

        {product.is_flat_rate === false && (
          <div className="underline">
            <Button
              text="Estimate Shipping"
              type="text"
              noXPadding
              onClick={() => setOpenModal('estimate-shipping')}
            />
          </div>
        )}

        <h3>
          Ships From{' '}
          {product.country_code === 'US'
            ? 'US'
            : countries[product.country_code as keyof typeof countries]?.name ??
              product.country_code}
        </h3>

        <AcceptedCards />

        <p className="text-[1.5rem]">
          GearFocus keeps your payment information secure.<br></br>GearFocus
          sellers never receive your credit card information.
        </p>
      </div>

      <ReturnPolicyModal
        isOpen={openModal === 'returnPolicy'}
        dismiss={() => setOpenModal('')}
        returnPolicy={seller?.return_policy}
        resolvedPolicy={resolvedReturnPolicy}
      />
      <ProtectionPolicyModal
        isOpen={openModal === 'protectionPlan'}
        dismiss={() => setOpenModal('')}
        content={protectionPolicyContent}
      />
      <ReportItemModal
        isUser={false}
        itemId={product.id}
        isOpen={openModal === 'reportItem'}
        dismiss={() => setOpenModal('')}
      />
      <ShareModal
        isOpen={openModal === 'share'}
        title={product.title}
        thumb={product.thumbnail}
        dismiss={() => setOpenModal('')}
      />
      <EstimateShippingModal
        isOpen={openModal === 'estimate-shipping'}
        product_id={product.id}
        dismiss={() => setOpenModal('')}
      />
      <ProductSchema product={product} pricing={storePrice ?? undefined} />
    </div>
  );
};
